import React, { useState } from 'react';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AppFooter from '@core/components/AppFooter';
import AuthBackToHome from '@features/auth/components/AuthBackToHome';
import { ErrorResponse } from '@core/types/errorResponse';
import { registerAsync } from '@features/auth/services/registerSvc';
import { RegisterRequest } from '@features/auth/types/registerRequest';
import { useApiError } from '@infrastructure/api/hooks/useApiError';
import '../login/login.css';
import loginImage from '../login/ntcog-logo.png';

interface CompleteRegistrationProps {
  email: string;
  verificationCode: string;
  invitationCode: string;
  onBack: () => void;
}

type PersonalInfoForm = Omit<RegisterRequest, 'verification_code' | 'email'>;
type FieldErrors = Partial<Record<keyof PersonalInfoForm | 'general', string[]>>;

const CompleteRegistration: React.FC<CompleteRegistrationProps> = ({ 
  email, 
  verificationCode,
  onBack 
}) => {
  const [form, setForm] = useState<PersonalInfoForm>({ 
    password: '', 
    password_confirmation: '', 
    firstname: '', 
    lastname: '', 
    trn: '' 
  });
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<FieldErrors>({});
  const showError = useApiError();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));

    // Clear error when field changes
    if (formErrors[name as keyof PersonalInfoForm]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[name as keyof PersonalInfoForm];
        return updated;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    
    if (!form.firstname.trim()) {
      errors.firstname = ['First name is required'];
    }
    
    if (!form.lastname.trim()) {
      errors.lastname = ['Last name is required'];
    }
    
    if (!form.password) {
      errors.password = ['Password is required'];
    } else if (form.password.length < 8) {
      errors.password = ['Password must be at least 8 characters long'];
    }
    
    if (form.password !== form.password_confirmation) {
      errors.password_confirmation = ['Passwords do not match'];
    }
    
    if (!form.trn.trim()) {
      errors.trn = ['TRN is required'];
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      // Build the complete registration request
      const registrationData: RegisterRequest = {
        verification_code: verificationCode,
        email: email,
        ...form
      };

      const response = await registerAsync(registrationData);

      if (response.isSuccess()) {
        setFormErrors({});
        navigate('/pending-invitations');
      } else {
        const error = response.getError() as ErrorResponse;
        setFormErrors((error?.errors as FieldErrors) ?? {});
        showError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper min-vh-100 d-flex flex-column">
      <AuthBackToHome />
      <main
        id="main"
        className="flex-grow-1 d-flex align-items-center justify-content-center py-4"
        tabIndex={-1}
        data-testid="register-complete-page"
      >
        <Container fluid>
          <Row className="justify-content-center">
            <Col xs={12} md={11} lg={10} xl={8}>
              <Card
                className="login-card shadow-lg border-0"
                style={{ maxWidth: '1000px' }}
                data-testid="register-complete-card"
              >
                <Row className="g-0">
                  <Col
                    md={5}
                    className="d-none d-md-flex align-items-center justify-content-center login-image p-4 bg-light"
                    data-testid="register-complete-illustration-panel"
                  >
                    <img src={loginImage} alt="NTCOG Logo" className="img-fluid" style={{ maxWidth: '250px' }} />
                  </Col>

                  <Col md={7} className="d-flex align-items-center">
                    <Card.Body className="p-4 p-md-5 w-100 auth-card__body">
                      <h1 className="h4 mb-2 text-center fw-bold text-dark auth-page-heading">Complete Registration</h1>
                      <p className="text-center text-muted mb-3 small">
                        Step 2 of 2: Personal information
                      </p>

                      <div className="mb-3 text-center">
                        <p className="mb-0 small">
                          <strong>Email:</strong>
                          <span className="text-muted d-block" data-testid="register-complete-email">
                            {email}
                          </span>
                        </p>
                      </div>

                      <Form
                        noValidate
                        onSubmit={handleSubmit}
                        className="login-form"
                        aria-label="Complete registration form"
                        data-testid="register-complete-form"
                      >
                        <Row className="g-2">
                          <Col xs={12} sm={6}>
                            <Form.Group className="mb-3" controlId="firstname">
                              <Form.Label className="small mb-1">First Name <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                type="text"
                                name="firstname"
                                placeholder="First Name"
                                value={form.firstname}
                                onChange={handleChange}
                                isInvalid={!!formErrors.firstname}
                                data-testid="register-firstname-input"
                              />
                              <Form.Control.Feedback type="invalid" role="alert">
                                {formErrors.firstname?.[0]}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col xs={12} sm={6}>
                            <Form.Group className="mb-3" controlId="lastname">
                              <Form.Label className="small mb-1">Last Name <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                type="text"
                                name="lastname"
                                placeholder="Last Name"
                                value={form.lastname}
                                onChange={handleChange}
                                isInvalid={!!formErrors.lastname}
                                data-testid="register-lastname-input"
                              />
                              <Form.Control.Feedback type="invalid" role="alert">
                                {formErrors.lastname?.[0]}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Form.Group className="mb-3" controlId="trn">
                          <Form.Label className="small mb-1">TRN (Tax Registration Number) <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="trn"
                            placeholder="TRN"
                            value={form.trn}
                            onChange={handleChange}
                            isInvalid={!!formErrors.trn}
                            data-testid="register-trn-input"
                          />
                          <Form.Control.Feedback type="invalid" role="alert">
                            {formErrors.trn?.[0]}
                          </Form.Control.Feedback>
                          <Form.Text className="text-muted small">
                            Enter your unique Tax Registration Number
                          </Form.Text>
                        </Form.Group>

                        <Row className="g-2">
                          <Col xs={12} sm={6}>
                            <Form.Group className="mb-3" controlId="password">
                              <Form.Label className="small mb-1">Password <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                type="password"
                                name="password"
                                placeholder="Password"
                                value={form.password}
                                onChange={handleChange}
                                autoComplete="new-password"
                                isInvalid={!!formErrors.password}
                                data-testid="register-password-input"
                              />
                              <Form.Control.Feedback type="invalid" role="alert">
                                {formErrors.password?.[0]}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>

                          <Col xs={12} sm={6}>
                            <Form.Group className="mb-3" controlId="password_confirmation">
                              <Form.Label className="small mb-1">Confirm Password <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                type="password"
                                name="password_confirmation"
                                placeholder="Confirm Password"
                                value={form.password_confirmation}
                                onChange={handleChange}
                                autoComplete="new-password"
                                isInvalid={!!formErrors.password_confirmation}
                                data-testid="register-password-confirmation-input"
                              />
                              <Form.Control.Feedback type="invalid" role="alert">
                                {formErrors.password_confirmation?.[0]}
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>

                        <div className="d-flex flex-column flex-sm-row gap-2 mt-4 mb-3">
                          <Button
                            type="button"
                            variant="outline-secondary"
                            size="lg"
                            className="w-100 flex-sm-fill"
                            onClick={onBack}
                            disabled={loading}
                            data-testid="register-back-button"
                          >
                            Back
                          </Button>
                          <Button
                            type="submit"
                            variant="primary"
                            size="lg"
                            className="w-100 flex-sm-fill"
                            disabled={loading}
                            aria-busy={loading}
                            data-testid="register-submit-button"
                          >
                            {loading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                />
                                Creating Account…
                              </>
                            ) : (
                              'Create Account'
                            )}
                          </Button>
                        </div>

                        <div className="auth-secondary-link-wrap">
                          <Button
                            variant="link"
                            className="text-muted p-0 forgot-password auth-secondary-link"
                            onClick={() => navigate('/login')}
                            data-testid="register-complete-login-link"
                          >
                            Already have an account? Login
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Container>
      </main>
      <AppFooter />
    </div>
  );
};

export default CompleteRegistration;